import React from 'react';
import core, { API, App, View } from '@gin-betting/core';
import { Filters } from '@gin-betting/filters';

import features from 'services/feature-manager';
import { SPORTS_ORGANIZATION } from '../../../constants';
import { sortByTopTournaments } from '../../../utils';
import CategorizerRowHeader from '../CategorizerRowHeader';
import { CategorizerSportSummaries } from '../CategorizerSportSummaries';
import { CheckRowList } from './CheckRowList';
import { LocationRow } from './LocationRow';
import { OrganizationRow } from './OrganizationRow';
import useCategorizerRow from './useCategorizerRow';

import './categorizerRow.scss';

interface Props {
    selectedTournamentIds: string[];
    outrightsCount: number | undefined;
    categorizerItem: API.CategorizerItem;
    isOpened: boolean;
    isSelected: boolean;
    onSportSelected: (categorizerItem: API.CategorizerItem) => void;
    onSportPreviewClick: (sportId: string, type: Filters.CheckGameType) => void;
    onTournamentIdsChange: (
        tournamentsIds: string[],
        sportIdToRemove?: string
    ) => void;
    isLive: boolean;
}

const {
    utils: { checkOnRoute },
} = core;

const CategorizerRow: React.FC<Props> = ({
    selectedTournamentIds,
    onTournamentIdsChange: handleTournamentIdsChange,
    categorizerItem,
    isOpened,
    onSportPreviewClick,
    onSportSelected,
    isSelected,
    outrightsCount,
    isLive,
}) => {
    const {
        sport,
        isAllItemsInSportChecked,
        isTournamentManuallyChecked,
        handleSportPreviewClick,
        handleSportSelected,
        handleTournamentsCheck,
    } = useCategorizerRow({
        selectedTournamentIds,
        categorizerItem,
        isOpened,
        onSportPreviewClick,
        onSportSelected,
        handleTournamentIdsChange,
        isSelected,
    });

    if (!sport) return null;

    const { id: sportId } = sport;

    return (
        <React.Fragment>
            <CategorizerRowHeader
                label={sport.name}
                gameId={sportId}
                isOpened={isOpened}
                isSelected={isSelected}
                onClick={handleSportPreviewClick}
                onSelect={handleSportSelected}
                isLive={isLive}
            />

            {isOpened && (
                <div styleName="filters">
                    {!checkOnRoute(App.Routes.RESULTS_LIST) && (
                        <CategorizerSportSummaries
                            sportId={sportId}
                            outrightsCount={outrightsCount}
                        />
                    )}

                    {renderRowContent({
                        sport,
                        categorizerItem,
                        selectedTournamentIds,
                        isAllItemsInSportChecked,
                        isTournamentManuallyChecked,
                        handleTournamentsCheck,
                        handleTournamentIdsChange,
                    })}
                </div>
            )}
        </React.Fragment>
    );
};

function checkGroupedByLocation(sport: View.Sport): boolean {
    return sport.tags.includes(API.SportTag.CLASSIC);
}

interface RenderRowContentInput
    extends Pick<Props, 'categorizerItem' | 'selectedTournamentIds'> {
    sport: View.Sport;
    isAllItemsInSportChecked: boolean;
    isTournamentManuallyChecked: boolean;
    handleTournamentsCheck: (nextTournamentIds: string[]) => void;
    handleTournamentIdsChange: (tournamentIds: string[]) => void;
}

function renderRowContent({
    sport,
    categorizerItem,
    selectedTournamentIds,
    isAllItemsInSportChecked,
    isTournamentManuallyChecked,
    handleTournamentsCheck,
    handleTournamentIdsChange,
}: RenderRowContentInput): JSX.Element {
    const isActiveTopTournaments = features.isActive(
        features.list.CategorizerTopTournaments
    );

    const { children: childrenCategorizerItem } = categorizerItem;

    const renderCheckRowList = (
        categorizerItemChildren: API.CategorizerItem[]
    ): JSX.Element => (
        <CheckRowList
            items={categorizerItemChildren}
            isTournamentManuallyChecked={isTournamentManuallyChecked}
            isAllItemsInSportChecked={isAllItemsInSportChecked}
            selectedTournamentIds={selectedTournamentIds}
            hasSiblingSubgroups={false}
            onTournamentsChecked={handleTournamentsCheck}
        />
    );

    switch (true) {
        case SPORTS_ORGANIZATION.includes(sport.id): {
            return (
                <React.Fragment>
                    <OrganizationRow
                        items={
                            isActiveTopTournaments
                                ? sortByTopTournaments(childrenCategorizerItem)
                                : childrenCategorizerItem
                        }
                        sportId={sport.id}
                        topCategorizerItem={categorizerItem}
                        selectedTournamentIds={selectedTournamentIds}
                        isAllItemsInSportChecked={isAllItemsInSportChecked}
                        onTournamentIdsChange={handleTournamentIdsChange}
                    />
                </React.Fragment>
            );
        }

        case checkGroupedByLocation(sport): {
            return (
                <React.Fragment>
                    <LocationRow
                        sportId={sport.id}
                        topCategorizerItem={categorizerItem}
                        tournaments={
                            isActiveTopTournaments
                                ? sortByTopTournaments(childrenCategorizerItem)
                                : childrenCategorizerItem
                        }
                        selectedTournamentIds={selectedTournamentIds}
                        isTournamentManuallyChecked={
                            isTournamentManuallyChecked
                        }
                        isAllItemsInSportChecked={isAllItemsInSportChecked}
                        onTournamentsChecked={handleTournamentsCheck}
                        onTournamentIdsChange={handleTournamentIdsChange}
                    />
                </React.Fragment>
            );
        }

        default: {
            const sortedItems = isActiveTopTournaments
                ? sortByTopTournaments(childrenCategorizerItem)
                : childrenCategorizerItem;

            return renderCheckRowList(sortedItems);
        }
    }
}

export default CategorizerRow;
